import styled from '@emotion/styled';
import css from '@emotion/css';
import { KizenTypography, monoFontFamily } from '__app/typography';
import { borderRadii } from '__app/spacing';
import TableScrollContainer from '__components/Tables/ScrollContainer';
import { BOTTOM_BAR_HEIGHT, MAX_WIDTH, MAX_WIDTH_OFFSET } from './constants';
import { underlineDottedStyling } from '__components/Kizen/EditableText/styles';

export const richTextStyle = css`
  & *,
  & li::marker {
    font-family: inherit;
    font-size: inherit;
    line-height: 1.5em;
  }

  & p {
    margin-bottom: 0.5em;
  }

  & p * {
    line-height: inherit;
  }

  & h1 {
    font-size: 2em;
    font-weight: 600;
    margin: 0.67em 0;
  }

  & h2 {
    font-size: 1.5em;
    font-weight: 600;
    margin: 0.83em 0;
  }

  & h3 {
    font-size: 1.17em;
    font-weight: 600;
    margin: 1em 0;
  }

  & h4 {
    font-size: 1em;
    font-weight: 600;
    margin: 1.33em 0;
  }

  & h5 {
    font-size: 0.83em;
    font-weight: 600;
    margin: 1.67em 0;
  }

  & h6 {
    font-size: 0.67em;
    font-weight: 600;
    margin: 2.33em 0;
  }

  & hr {
    box-sizing: border-box;
    border-width: 0;
    border-style: solid;
    border-color: inherit;
    margin: 0.5em 0;
    color: var(--color-font-placeholder);
    border-top-width: 1px;
  }

  & code {
    font-family: ${monoFontFamily};
    font-size: 1em;
    font-weight: 400;
    background-color: var(--color-background-light);
    padding: 5px;
    color: var(--color-font-primary);
    border-radius: ${borderRadii.small};
  }

  & pre {
    padding: 10px;
    background-color: var(--color-background-light);
    border-radius: ${borderRadii.small};
    border: 1px solid var(--color-border-medium);
    & > code {
      padding: 0;
      background-color: unset;
      border-radius: 0;
      white-space: pre-wrap;
      word-break: break-all;
    }
  }

  & a {
    color: var(--color-button-secondary-default);
    &:hover {
      color: var(--color-button-secondary-hover);
    }
  }
`;

export const MarkdownEditorWrapper = styled.div<{ toolbarIsHidden?: boolean }>`
  border: 1px solid var(--color-border-medium);

  background: var(--color-background-white);

  border-bottom-right-radius: ${borderRadii.small};
  border-bottom-left-radius: ${borderRadii.small};
  ${({ toolbarIsHidden }) =>
    toolbarIsHidden &&
    css`
      border-radius: ${borderRadii.small};
    `}

  & .tiptap.ProseMirror {
    ${richTextStyle}

    padding: 10px;
    outline: none;
    word-break: break-word;

    & > * + * {
      margin-top: 0;
    }

    font-family: 'Proxima Nova', sans-serif;
    font-size: 14px;
  }
`;

export const StyledInlineMarkdown = styled(KizenTypography)`
  width: fit-content;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  min-width: 12px;
  & * {
    pointer-events: none;
    font-size: 14px;
    display: inline-block;
    margin: 0;
  }
  & > *,
  && > p {
    margin-right: 3px;
  }
  & a {
    color: var(--color-background-darker);
    text-decoration: none;
  }
  & pre {
    display: inline-block;
    margin: 0;
    padding: 0;
    font-family: inherit;
    font-size: 14px;
    white-space: normal;
    color: var(--color-background-darker);
    vertical-align: bottom;
    & > code {
      font-family: inherit;
      white-space: nowrap;
    }
  }

  & br {
    display: none;
  }

  & hr {
    width: 24px;
    border-color: var(--color-background-darker);
  }
`;

export const markdownPreviewStyles = ({
  color = 'var(--color-font-primary)',
}: {
  color?: string;
}) => css`
  & p,
  p + p {
    margin: 0;
    margin-bottom: 0.5em;
    line-height: 1.15em;
    color: ${color};
    & * {
      font-size: 1em;
      line-height: 1.15em;
      color: ${color};
    }
  }
  & p:empty {
    height: 1.15em;
  }

  & pre {
    font-family: inherit;
    overflow: unset;
    white-space: pre-wrap;
    color: ${color};

    & > code {
      font-family: inherit;
      word-break: break-all;
    }
  }

  & hr {
    margin: 0.5em 0;
    border-color: ${color};
  }
`;

export const StyledInlineMarkdownWrapper = styled.div`
  width: 100%;
  display: flex;
  & p + p {
    margin: 0;
  }
`;

export const StyledInlineMarkdownContainer = styled.div<{
  disabled?: boolean;
  error?: boolean;
}>`
  width: 100%;
  display: flex;
  ${underlineDottedStyling}
  padding-bottom: 7px;
  &:not(:hover) {
    background-image: none;
  }

  tr:hover &:not([disabled]):not(:hover) {
    background-image: repeating-linear-gradient(
      to right,
      var(--color-border-dark) 0 5px,
      transparent 5px 10px
    );
  }
`;

export const StyledBottomBar = styled.div<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
  width: 100%;
  height: ${BOTTOM_BAR_HEIGHT}px;
  padding: 8px;
  ${({ disabled }) =>
    disabled
      ? css`
          color: var(--color-border-medium);
          background-color: var(--color-background-light);
        `
      : css`
          color: var(--color-background-darker);
        `};
`;

export const StyledScrollContainer = styled(TableScrollContainer)<{
  readonly?: boolean;
  isLoadingAI?: boolean;
}>`
  & .gradient-vertical-container {
    & > div:first-of-type,
    & > div:last-of-type {
      position: absolute;
      width: calc(100% - 8px);
      z-index: 1;
    }
    & > div:last-of-type {
      bottom: ${({ readonly }) => (readonly ? 0 : BOTTOM_BAR_HEIGHT)}px;
    }
  }

  ${({ isLoadingAI }) =>
    isLoadingAI &&
    css`
      filter: blur(2px);
    `}
`;

export const StyledOverlayTrigger = styled.div`
  height: 0;
  align-self: flex-start;
`;

export const StyledOverlayWrapper = styled.div<{ initialWidth?: number }>`
  ${({ initialWidth = MAX_WIDTH }) => css`
    width: min(
      ${initialWidth}px,
      calc(100vw - var(--app-inset-right, 0px) - ${MAX_WIDTH_OFFSET}px)
    );
  `}
`;
